import { TIER_CLASSES, TIER_LABELS } from '@busy/core';
import type { Tier } from '@busy/api';

// Architecture decision #1 (SESSION_MEMORY): single tier-badge component.
// Adding a new tier = updating TIER_CLASSES + TIER_LABELS in @busy/core, nothing else.
// DO NOT inline tier badge styling anywhere else in the app.
//
// `label` overrides the default English label — pass the i18n-resolved string
// (`t('tier.${tier}')`) from the consumer. The @busy/ui package stays UI-only and
// doesn't depend on the mobile app's i18n hook.
export function TierBadge({
  tier,
  size = 'md',
  label,
}: {
  tier: Tier;
  size?: 'sm' | 'md';
  label?: string;
}) {
  const cls = TIER_CLASSES[tier];
  const sizing = size === 'sm' ? 'text-[10px] px-1.5 py-0.5 gap-1' : 'text-xs px-2 py-1 gap-1.5';
  const iconSize = size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3';
  return (
    <span
      className={`inline-flex items-center font-semibold uppercase tracking-wide rounded ${sizing} ${cls.bg} ${cls.text} ${cls.ring ?? ''}`}
    >
      <TierIcon tier={tier} className={iconSize} />
      {label ?? TIER_LABELS[tier]}
    </span>
  );
}

// Tier-matched icons — paths ported from docs/DEMO_REFERENCE.html:8030-8036.
// at-risk = alert triangle, vip = crown, regular = checkmark,
// new = sparkles, active = person silhouette.
function TierIcon({ tier, className }: { tier: Tier; className?: string }) {
  switch (tier) {
    case 'at-risk':
      return (
        <svg
          className={className}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case 'vip':
      return (
        <svg
          className={className}
          viewBox="0 0 24 24"
          fill="currentColor"
          stroke="none"
          aria-hidden
        >
          <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm0 3v-1h14v1c0 .55-.45 1-1 1H6c-.55 0-1-.45-1-1z" />
        </svg>
      );
    case 'regular':
      return (
        <svg
          className={className}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    case 'new':
      return (
        <svg
          className={className}
          viewBox="0 0 24 24"
          fill="currentColor"
          stroke="none"
          aria-hidden
        >
          <path d="M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2zm6 12l.85 2.55L21 17.5l-2.15.95L18 21l-.85-2.55L15 17.5l2.15-.95L18 14zM6 14l.85 2.55L9 17.5l-2.15.95L6 21l-.85-2.55L3 17.5l2.15-.95L6 14z" />
        </svg>
      );
    case 'active':
      return (
        <svg
          className={className}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      );
  }
}
